import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiUrl } from '../config/api';

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const ResetPassword = () => {
  const q = useQuery();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [status, setStatus] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setEmail(q.get('email') || '');
    setToken(q.get('token') || '');
  }, [q]);

  // Live rules checklist (same rules as ChangePassword)
  const checklist = [
    { label: 'At least 8 characters long', ok: (password || '').length >= 8 },
    { label: 'Contains at least one uppercase letter (A–Z)', ok: /[A-Z]/.test(password || '') },
    { label: 'Contains at least one lowercase letter (a–z)', ok: /[a-z]/.test(password || '') },
    { label: 'Contains at least one number (0–9)', ok: /[0-9]/.test(password || '') },
    { label: 'Contains at least one special character (e.g. ! @ # $ % ^ & *)', ok: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password || '') },
    { label: 'Passwords match', ok: (confirm || '').length > 0 && confirm === password },
  ];

  const submit = async (e) => {
    e.preventDefault();
    setStatus(null);
    if (password !== confirm) {
      setStatus({ ok: false, msg: 'Passwords do not match' });
      return;
    }
    // Client-side complexity validation to match first-time change UI
    const rules = [
      { test: (p) => typeof p === 'string' && p.length >= 8, msg: 'Password must be at least 8 characters long' },
      { test: (p) => /[A-Z]/.test(p), msg: 'Password must include at least one uppercase letter' },
      { test: (p) => /[a-z]/.test(p), msg: 'Password must include at least one lowercase letter' },
      { test: (p) => /[0-9]/.test(p), msg: 'Password must include at least one number' },
      { test: (p) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(p), msg: 'Password must include at least one special character' },
    ];
    for (const r of rules) {
      if (!r.test(password)) {
        setStatus({ ok: false, msg: r.msg });
        return;
      }
    }
    setLoading(true);
    try {
      const res = await axios.post(apiUrl('/api/auth/reset-password'), { email, token, newPassword: password });
      setStatus({ ok: true, msg: res.data?.message || 'Password reset successful' });
      setTimeout(() => navigate('/login'), 1500);
    } catch (e) {
      setStatus({ ok: false, msg: e?.response?.data?.error || 'Reset failed' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950 flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white dark:bg-slate-800 rounded-lg shadow p-6">
        <h2 className="text-center text-xl font-semibold text-gray-800 dark:text-slate-100 mb-4">Reset your password</h2>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Email</label>
            <input type="email" value={email} onChange={e=>setEmail(e.target.value)} required className={`w-full ${email ? '' : 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20'}`} />
          </div>
          <div className="relative">
            <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">New Password</label>
            <input type={showNew ? 'text' : 'password'} value={password} onChange={e=>setPassword(e.target.value)} required className={`w-full pr-10 ${password ? '' : 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20'}`} />
            <button type="button" onClick={()=>setShowNew(v=>!v)} className="absolute right-2 top-8 text-gray-600 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200">{showNew ? 'Hide' : 'Show'}</button>
          </div>
          <div className="relative">
            <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Confirm Password</label>
            <input type={showConfirm ? 'text' : 'password'} value={confirm} onChange={e=>setConfirm(e.target.value)} required className={`w-full pr-10 ${confirm ? '' : 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20'}`} />
            <button type="button" onClick={()=>setShowConfirm(v=>!v)} className="absolute right-2 top-8 text-gray-600 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200">{showConfirm ? 'Hide' : 'Show'}</button>
          </div>
          {/* Password requirements with live ticks */}
          <div className="p-3 rounded border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-sm">
            Your new password must meet all of the following requirements:
            <ul className="mt-2 space-y-1">
              {checklist.map((r, i) => (
                <li key={i} className={`flex items-center gap-2 ${r.ok ? 'text-green-700 dark:text-green-400' : 'text-gray-600 dark:text-slate-400'}`}>
                  <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold ${r.ok ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400' : 'bg-gray-200 dark:bg-slate-600 text-gray-600 dark:text-slate-400'}`}>
                    {r.ok ? '✓' : '•'}
                  </span>
                  <span>{r.label}</span>
                </li>
              ))}
            </ul>
          </div>
          <button type="submit" disabled={loading} className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 disabled:opacity-60">{loading ? 'Resetting…' : 'Reset Password'}</button>
        </form>
        {status && (
          <div className={`mt-4 text-sm text-center ${status.ok ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{status.msg}</div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
